import { useEffect } from 'react';
import usePlayer from '../contexts/usePlayer';

/**
 * MediaSession component - Handles browser Media Session API integration
 * for lock screen controls, headphone buttons, and system media controls.
 *
 * Renders nothing - just sets up event handlers and syncs state.
 * Only mounted when 'mediaSession' in navigator (checked in App.tsx).
 */
const MediaSession = () => {
  const {
    nowPlaying,
    currentPlaylist,
    isPlaying,
    currentTime,
    playbackRate,
    play,
    pause,
    previousTrack,
    nextTrack,
    seekForward,
    seekBackward,
    seekTo,
  } = usePlayer();

  // Set up action handlers
  useEffect(() => {
    navigator.mediaSession.setActionHandler('play', () => play());
    navigator.mediaSession.setActionHandler('pause', pause);
    navigator.mediaSession.setActionHandler('previoustrack', previousTrack);
    navigator.mediaSession.setActionHandler('nexttrack', nextTrack);
    navigator.mediaSession.setActionHandler('seekbackward', seekBackward);
    navigator.mediaSession.setActionHandler('seekforward', seekForward);
    navigator.mediaSession.setActionHandler('seekto', seekTo);

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekto', null);
    };
  }, [
    play,
    pause,
    previousTrack,
    nextTrack,
    seekBackward,
    seekForward,
    seekTo,
  ]);

  // Update metadata when nowPlaying changes
  useEffect(() => {
    if (!nowPlaying) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: nowPlaying.title,
      artist: 'Earmark.fm',
      album: currentPlaylist?.title || 'Earmark.fm',
      artwork: nowPlaying.imageUrl
        ? [
            { src: nowPlaying.imageUrl, sizes: '96x96', type: 'image/jpeg' },
            { src: nowPlaying.imageUrl, sizes: '128x128', type: 'image/jpeg' },
            { src: nowPlaying.imageUrl, sizes: '192x192', type: 'image/jpeg' },
            { src: nowPlaying.imageUrl, sizes: '256x256', type: 'image/jpeg' },
            { src: nowPlaying.imageUrl, sizes: '384x384', type: 'image/jpeg' },
            { src: nowPlaying.imageUrl, sizes: '512x512', type: 'image/jpeg' },
          ]
        : undefined,
    });
  }, [nowPlaying, currentPlaylist?.title]);

  // Update playback state when isPlaying changes
  useEffect(() => {
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Update position state periodically
  useEffect(() => {
    if (!nowPlaying?.secondsTotal) return;

    navigator.mediaSession.setPositionState({
      duration: nowPlaying.secondsTotal,
      playbackRate: playbackRate,
      position: Math.min(currentTime, nowPlaying.secondsTotal),
    });
  }, [currentTime, playbackRate, nowPlaying?.secondsTotal]);

  return null;
};

export default MediaSession;
